import { useEffect, useState } from 'react'
import { Clock, Star, StarOff, File as FileIcon, Folder as FolderIcon, Loader2, X } from 'lucide-react'
import { publicFileUrl } from '../config'
import { useRecentFiles, RecentFile } from '../hooks/useRecentFiles'

// Favorites are per-role so admin and team on the same browser never share
// pins. Role comes from the same localStorage key authStore persists to.
const LEGACY_FAVORITES_KEY = 'vendetta-favorites'

const currentRole = (): string => {
  try { return window.localStorage?.getItem('vendetta-role') || 'admin' } catch { return 'admin' }
}

const favoritesKey = (): string => `vendetta-favorites-${currentRole()}`

// Persist shape v2 — supports both folder and file favorites. On load we
// migrate the v1 shape (`string[]` of folder paths only) transparently.
interface FavoriteEntry {
  path: string
  name: string
  type: 'file' | 'folder'
}

interface Props {
  currentPath: string
  onNavigate: (path: string) => void
  refreshKey?: number
}

// Read the persisted favorites, migrating the legacy folder-only string[]
// format on the fly. Exported so external callers (FileItem) can toggle
// file favorites without duplicating the storage logic.
export function loadFavorites(): FavoriteEntry[] {
  try {
    // Admin inherits pre-namespace pins once (legacy single key); team starts
    // fresh so it never sees admin's favorites.
    const raw = localStorage.getItem(favoritesKey())
      ?? (currentRole() === 'admin' ? localStorage.getItem(LEGACY_FAVORITES_KEY) : null)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    return parsed
      .map((entry: unknown): FavoriteEntry | null => {
        // v1 legacy: plain folder path string
        if (typeof entry === 'string') {
          return { path: entry, name: entry.split('/').pop() || entry, type: 'folder' }
        }
        if (entry && typeof entry === 'object') {
          const e = entry as any
          if (typeof e.path === 'string' && typeof e.name === 'string' &&
              (e.type === 'file' || e.type === 'folder')) {
            return e as FavoriteEntry
          }
        }
        return null
      })
      .filter((x): x is FavoriteEntry => x !== null)
  } catch { return [] }
}

export function saveFavorites(list: FavoriteEntry[]) {
  const unique = new Map<string, FavoriteEntry>()
  for (const f of list) unique.set(`${f.type}:${f.path}`, f)
  try { localStorage.setItem(favoritesKey(), JSON.stringify(Array.from(unique.values()))) } catch { /* ignore */ }
  // Notify listeners in same tab — 'storage' event only fires cross-tab, so
  // components sharing this store rely on a custom event to re-read.
  window.dispatchEvent(new CustomEvent('vndta:favorites-changed'))
}

export function toggleFavorite(entry: FavoriteEntry) {
  const current = loadFavorites()
  const key = `${entry.type}:${entry.path}`
  const exists = current.some(f => `${f.type}:${f.path}` === key)
  const next = exists
    ? current.filter(f => `${f.type}:${f.path}` !== key)
    : [...current, entry]
  saveFavorites(next)
  return !exists
}

export function isFavorite(entry: { path: string; type: 'file' | 'folder' }): boolean {
  return loadFavorites().some(f => f.path === entry.path && f.type === entry.type)
}

// Recent uploads (backend-sourced) + pinned favorites (localStorage, both
// files and folders). Solo user's daily helper.
export function RecentAndFavoritesSidebar({ currentPath, onNavigate, refreshKey = 0 }: Props) {
  const { recent, loadingRecent } = useRecentFiles(refreshKey)
  const [favorites, setFavorites] = useState<FavoriteEntry[]>(() => loadFavorites())

  // Listen for favorites changes (from FileItem's star toggle in another tree branch).
  useEffect(() => {
    const refresh = () => setFavorites(loadFavorites())
    window.addEventListener('vndta:favorites-changed', refresh)
    return () => window.removeEventListener('vndta:favorites-changed', refresh)
  }, [])

  const currentFolderFavorite: FavoriteEntry | null = currentPath
    ? { path: currentPath, name: currentPath.split('/').pop() || currentPath, type: 'folder' }
    : null

  const currentFolderIsPinned = !!currentFolderFavorite && favorites.some(
    f => f.type === 'folder' && f.path === currentFolderFavorite.path
  )

  const toggleCurrentFolder = () => {
    if (!currentFolderFavorite) return
    toggleFavorite(currentFolderFavorite)
    setFavorites(loadFavorites())
  }

  const removeFavorite = (entry: FavoriteEntry, e: React.MouseEvent) => {
    e.stopPropagation()
    toggleFavorite(entry)
    setFavorites(loadFavorites())
  }

  const openEntry = (entry: FavoriteEntry) => {
    if (entry.type === 'folder') {
      onNavigate(entry.path)
    } else {
      // File favorite → open the public URL in a new tab. Navigating into a
      // file-manager view for a single file isn't quite right; the URL is what
      // the user wanted quick access to.
      window.open(publicFileUrl(entry.path), '_blank', 'noopener')
    }
  }

  const openFileParent = (file: RecentFile) => {
    const parent = file.path.split('/').slice(0, -1).join('/')
    onNavigate(parent)
  }

  return (
    <div className="space-y-4">
      {/* Favorites */}
      <div className="glass rounded-2xl p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Star className="h-4 w-4 text-yellow-400" />
            <h3 className="text-xs font-semibold uppercase tracking-wider text-dark-300">Favorites</h3>
          </div>
          {currentPath && (
            <button
              onClick={toggleCurrentFolder}
              className={`rounded-md p-1.5 transition ${
                currentFolderIsPinned
                  ? 'text-yellow-400 hover:bg-yellow-500/10'
                  : 'text-dark-500 hover:bg-white/5 hover:text-yellow-400'
              }`}
              title={currentFolderIsPinned ? 'Unpin this folder' : 'Pin this folder'}
            >
              {currentFolderIsPinned ? <StarOff className="h-3.5 w-3.5" /> : <Star className="h-3.5 w-3.5" />}
            </button>
          )}
        </div>

        {favorites.length === 0 ? (
          <p className="text-xs text-dark-500">
            Star the current folder (top-right), or right-click a file → Pin favorite.
          </p>
        ) : (
          <ul className="space-y-1">
            {favorites.map(entry => {
              const isActive = entry.type === 'folder' && entry.path === currentPath
              const Icon = entry.type === 'folder' ? FolderIcon : FileIcon
              const iconTone = entry.type === 'folder' ? 'text-blue-400' : 'text-dark-400'
              return (
                <li key={`${entry.type}:${entry.path}`} className="group flex items-center gap-1">
                  <button
                    onClick={() => openEntry(entry)}
                    className={`flex-1 flex items-center gap-2 rounded-lg px-2 py-1.5 text-left text-sm transition min-w-0 ${
                      isActive
                        ? 'bg-primary-500/10 text-primary-300'
                        : 'text-dark-300 hover:bg-white/5 hover:text-white'
                    }`}
                    title={entry.path}
                  >
                    <Icon className={`h-3.5 w-3.5 flex-shrink-0 ${iconTone}`} />
                    <span className="truncate">{entry.name}</span>
                  </button>
                  <button
                    onClick={(e) => removeFavorite(entry, e)}
                    className="rounded-md p-1 text-dark-500 opacity-0 transition hover:bg-white/5 hover:text-red-400 group-hover:opacity-100"
                    title="Remove from favorites"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </li>
              )
            })}
          </ul>
        )}
      </div>

      {/* Recent */}
      <div className="glass rounded-2xl p-4">
        <div className="flex items-center gap-2 mb-3">
          <Clock className="h-4 w-4 text-emerald-400" />
          <h3 className="text-xs font-semibold uppercase tracking-wider text-dark-300">Recent uploads</h3>
        </div>

        {loadingRecent && recent.length === 0 ? (
          <div className="flex items-center justify-center py-4 text-dark-400">
            <Loader2 className="h-4 w-4 animate-spin" />
          </div>
        ) : recent.length === 0 ? (
          <p className="text-xs text-dark-500">No files yet.</p>
        ) : (
          <ul className="space-y-1">
            {recent.map(file => {
              const days = Math.floor((Date.now() - new Date(file.uploadDate).getTime()) / (1000 * 60 * 60 * 24))
              const ago =
                days === 0 ? 'today'
                : days === 1 ? '1 day ago'
                : days < 30 ? `${days} days ago`
                : `${Math.floor(days / 30)} mo ago`
              return (
                <li key={file.id}>
                  <button
                    onClick={() => openFileParent(file)}
                    className="w-full flex items-center gap-2 rounded-lg px-2 py-1.5 text-left text-sm text-dark-300 transition hover:bg-white/5 hover:text-white"
                    title={`${file.path}\n${ago}`}
                  >
                    <FileIcon className="h-3.5 w-3.5 flex-shrink-0 text-dark-400" />
                    <span className="truncate flex-1">{file.name}</span>
                    <span className="text-[10px] text-dark-500 flex-shrink-0">{ago}</span>
                  </button>
                </li>
              )
            })}
          </ul>
        )}
      </div>
    </div>
  )
}
